<template>
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"/>
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100"
                                 leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                        <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                            <button type="button" class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none " @click="close">
                                <span class="sr-only">Close</span>
                                <XIcon class="h-6 w-6" aria-hidden="true"/>
                            </button>
                        </div>
                        <div class="sm:flex sm:items-start">
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">
                                    {{ t('verification.process') }}
                                </DialogTitle>
                                <div class="mt-[1px]">
                                    <p v-if="state.type === 'request'" class="text-sm text-gray-500">
                                        {{ t('verification.enterMobile') }}
                                    </p>
                                    <p v-if="state.type === 'verify'" class="text-sm text-gray-500">
                                        {{ t('verification.check') }}
                                    </p>
                                </div>

                                <div v-if="state.type === 'request'" class="mt-2 space-y-2 flex flex-col my-4">
                                    <label for="sms">{{ t('verification.mobile') }}</label>
                                    <input required @input="acceptNumber" class="flex rounded-md w-full px-4 py-2" name="sms" type="text" v-model="sms" placeholder="123-456-7890">
                                    <span class="text-fire text-xs font-bold" v-if="error.show">{{ error.message }}</span>
                                </div>

                                <div v-if="state.type === 'verify'" class="mt-2 my-4">
                                    <div v-if="success.show" class="flex flex-col space-y-3 justify-center items-center">
                                        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                            <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true"/>
                                        </div>
                                        <p>{{ success.message }}</p>
                                    </div>
                                    <div v-else class="space-y-2 flex flex-col ">
                                        <label for="code">{{ t('verification.code') }}</label>
                                        <input @input="limitNumber(4)" required class="flex rounded-md w-full px-4 py-2" name="code" type="text" maxlength="4" v-model="code" placeholder="1234">
                                        <span class="text-fire text-xs font-bold" v-if="error.show">{{ error.message }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button v-if="state.type === 'request'"
                                    type="button"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    @click="verify">
                                <span class="inline-flex space-x-4" v-if="loading">
                                    <span>{{ t('verification.sending') }}</span>
                                    <Spinner text-color="text-white" class=""/>
                                </span>
                                <span v-else>{{ t('verification.sendCode') }}</span>
                            </button>
                            <button v-if="state.type === 'verify' && !success.show"
                                    type="button"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    @click="verifyCode">
                                {{ t('verification.verify') }}
                            </button>
                            <button type="button"
                                    :class="[success.show ? 'border-green-300 bg-green-600 text-white hover:text-gray-500 focus:ring-green-500': 'border-gray-300 bg-white text-gray-700 hover:text-gray-500 focus:ring-indigo-500']"
                                    class="mt-3 w-full inline-flex justify-center rounded-md border shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                                    @click="close">
                                <span v-if="success.show">Done</span>
                                <span v-else>Cancel</span>
                            </button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
    <template v-if="success.show">
        <SmsVerificationSuccess/>
    </template>
</template>

<script>
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {CheckIcon, ExclamationIcon, XIcon} from '@heroicons/vue/outline'
import {onMounted, ref} from "vue";
import Spinner from "./Spinner";
import {useI18n} from "vue-i18n";
import SmsVerificationSuccess from "./SmsVerificationSuccess";

export default {
    name: 'SmsProcess',
    components: {
        SmsVerificationSuccess,
        CheckIcon,
        Spinner,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        ExclamationIcon,
        XIcon,
    },
    props: {
        open: {
            type: Boolean,
            default: false
        }
    },
    setup(props, {emit}) {
        const {t} = useI18n()
        const sms = ref('')
        const code = ref('')
        const loading = ref(false)
        const success = ref({
            show: false,
            message: ''
        })

        const state = ref({
            type: 'request',
            message: ''
        })

        onMounted(() => {
            init()
        })

        const close = () => {
            init();
            emit('close', true)
        }

        const init = () => {
            code.value = ''
            sms.value = ''
            loading.value = false
            state.value = {
                type: 'request',
                message: ''
            }
            resetError()
            resetSuccess()
        }


        const error = ref({show: false, message: ''});

        const acceptNumber = () => {
            const x = sms.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
            sms.value = !x[2] ? x[1] : x[1] + '-' + x[2] + (x[3] ? '-' + x[3] : '');
        }

        const limitNumber = (value) => {
            code.value = code.value.length > value ? code.value.substr(0, value) : code.value
        }

        const resetError = () => {
            error.value = {
                show: false,
                message: '',
            }
        }

        const resetSuccess = () => {
            success.value = {
                show: false,
                message: '',
            }
        }

        const verify = async () => {
            resetError()
            loading.value = true
            if (sms.value.length !== 12) {
                loading.value = false
                error.value = {
                    show: true,
                    message: t('verification.required'),
                }
                return;
            }

            try {
                await axios.post('/settings/sms/request', {phone: sms.value})

                state.value.type = 'verify'

                resetError()
            } catch (err) {
                error.value = {
                    show: true,
                    message: err.response.data.message,
                }
            } finally {
                loading.value = false
            }
        }

        const isValidCode = () => {
            if (code.value.length !== 4) {
                loading.value = false
                error.value = {
                    show: true,
                    message: t('verification.valid'),
                }
                return;
            }
        }

        const verifyCode = async () => {
            try {
                resetError()
                loading.value = true
                isValidCode()

                await axios.post('/settings/sms/verify', {code: code.value})
                success.value.show = true
                emit('close', true)

            } catch (err) {
                resetSuccess()
                error.value = {
                    show: true,
                    message: err.response.data.message,
                }
            } finally {
                loading.value = false
            }
        }

        return {
            acceptNumber,
            loading,
            verifyCode,
            success,
            verify,
            state,
            error,
            close,
            limitNumber,
            code,
            sms,
            t
        }
    },
}
</script>
